import type { Stage } from '@crm/shared';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import type { Opportunity } from '../../lib/api';
import { formatChfCompact } from '../../lib/format';

import { OpportunityCard } from './OpportunityCard';

type Props = {
  stage: Stage;
  index: number;
  label: string;
  opportunities: Opportunity[];
  sourceStage: Stage | null;
};

export function KanbanColumn({ stage, index, label, opportunities, sourceStage }: Props) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });

  const totalAmount = opportunities.reduce((acc, o) => acc + o.initial_expected_amount, 0);
  const isPotentialDropTarget = sourceStage !== null && sourceStage !== stage;
  const isActiveDropTarget = isOver && isPotentialDropTarget;

  return (
    <section
      ref={setNodeRef}
      data-testid={`kanban-column-${stage}`}
      className={
        'flex flex-col w-[300px] shrink-0 border-r border-platinum last:border-r-0 transition-colors ' +
        (isPotentialDropTarget ? 'bg-glacier/60' : '')
      }
      aria-label={`Stage ${label}, ${opportunities.length} opportunities`}
    >
      <header className="px-5 py-4 relative">
        {isActiveDropTarget && (
          <span
            aria-hidden="true"
            className="absolute left-0 top-0 bottom-0 w-[3px] bg-signal motion-safe:transition-opacity"
          />
        )}
        <div className="flex items-baseline gap-3">
          <span className="font-display text-2xl text-graphite leading-none">
            {String(index).padStart(2, '0')}
          </span>
          <span className="label-uppercase">— {label}</span>
        </div>
        <p className="mt-2 text-xs font-tabular text-ink-secondary">
          <span className="text-ink font-medium">{opportunities.length}</span> opps ·{' '}
          {formatChfCompact(totalAmount)}
        </p>
      </header>

      <SortableContext
        items={opportunities.map((o) => o.id)}
        strategy={verticalListSortingStrategy}
      >
        <ol
          className={
            'flex-1 px-3 py-2 space-y-3 overflow-y-auto min-h-[120px] transition-colors ' +
            (isActiveDropTarget ? 'bg-glacier' : '')
          }
        >
          {opportunities.length === 0 && (
            <li className="text-sm italic text-ink-tertiary px-2 py-4">
              No opportunities at this stage.
            </li>
          )}
          {opportunities.map((o) => (
            <li key={o.id}>
              <OpportunityCard opportunity={o} />
            </li>
          ))}
        </ol>
      </SortableContext>
    </section>
  );
}
